/**
 * RENDER module — the noir grade shader.
 *
 * Single fullscreen pass, applied in display space after tone mapping:
 *   1. luminance B&W conversion (uBwMix)
 *   2. levels crush (uBlackPoint / uWhitePoint) — deep blacks, hot highlights
 *   3. slight sepia lift (uSepiaAmount, uSepiaTint)
 *   4. animated time-seeded film grain (uGrainIntensity, uGrainSizePx)
 *   5. vignette (uVignetteStrength)
 *
 * Every uniform is driven from NoirPostConfig; zero values disable each
 * stage cleanly (the pass becomes an identity copy).
 */
import { Vector2, Vector3 } from 'three'
import type { IUniform } from 'three'
import {
  GRAIN_HIGHLIGHT_SUPPRESSION,
  GRAIN_SEED_SPREAD,
  GRAIN_TIME_DRIFT,
  SEPIA_TINT_B,
  SEPIA_TINT_G,
  SEPIA_TINT_R,
  VIGNETTE_INNER_RADIUS,
  VIGNETTE_OUTER_RADIUS
} from './constants'

export interface NoirShaderUniforms {
  readonly [uniform: string]: IUniform
  readonly tDiffuse: IUniform<null>
  readonly uTime: IUniform<number>
  readonly uResolution: IUniform<Vector2>
  readonly uBwMix: IUniform<number>
  readonly uBlackPoint: IUniform<number>
  readonly uWhitePoint: IUniform<number>
  readonly uSepiaAmount: IUniform<number>
  readonly uSepiaTint: IUniform<Vector3>
  readonly uGrainIntensity: IUniform<number>
  readonly uGrainSizePx: IUniform<number>
  readonly uVignetteStrength: IUniform<number>
}

export const createNoirShaderUniforms = (): NoirShaderUniforms => ({
  tDiffuse: { value: null },
  uTime: { value: 0 },
  uResolution: { value: new Vector2(1, 1) },
  uBwMix: { value: 1 },
  uBlackPoint: { value: 0 },
  uWhitePoint: { value: 1 },
  uSepiaAmount: { value: 0 },
  uSepiaTint: { value: new Vector3(SEPIA_TINT_R, SEPIA_TINT_G, SEPIA_TINT_B) },
  uGrainIntensity: { value: 0 },
  uGrainSizePx: { value: 1 },
  uVignetteStrength: { value: 0 }
})

export const NOIR_VERTEX_SHADER = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

export const NOIR_FRAGMENT_SHADER = /* glsl */ `
uniform sampler2D tDiffuse;
uniform float uTime;
uniform vec2 uResolution;
uniform float uBwMix;
uniform float uBlackPoint;
uniform float uWhitePoint;
uniform float uSepiaAmount;
uniform vec3 uSepiaTint;
uniform float uGrainIntensity;
uniform float uGrainSizePx;
uniform float uVignetteStrength;

varying vec2 vUv;

const vec3 LUMA_WEIGHTS = vec3(0.2126, 0.7152, 0.0722);
const float GRAIN_TIME_DRIFT = ${GRAIN_TIME_DRIFT.toFixed(4)};
const float GRAIN_SEED_SPREAD = ${GRAIN_SEED_SPREAD.toFixed(1)};
const float GRAIN_HIGHLIGHT_SUPPRESSION = ${GRAIN_HIGHLIGHT_SUPPRESSION.toFixed(2)};
const float VIGNETTE_INNER = ${VIGNETTE_INNER_RADIUS.toFixed(2)};
const float VIGNETTE_OUTER = ${VIGNETTE_OUTER_RADIUS.toFixed(2)};

// Cheap stable white noise (Dave Hoskins hash without sine).
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 443.8975);
  p3 += dot(p3, p3.yzx + 19.19);
  return fract((p3.x + p3.y) * p3.z);
}

void main() {
  vec4 texel = texture2D(tDiffuse, vUv);
  vec3 color = texel.rgb;

  // 1. Luminance black-and-white conversion — with chroma preservation for
  // strongly saturated sources (neon tubes, sodium pools, lit windows) so
  // the grade reads as selective-color noir instead of flat mono. Neon is
  // never white.
  float luma = dot(color, LUMA_WEIGHTS);
  float maxChannel = max(color.r, max(color.g, color.b));
  float minChannel = min(color.r, min(color.g, color.b));
  float saturation = maxChannel - minChannel;
  float chromaKeep = smoothstep(0.08, 0.42, saturation) * 0.8;
  color = mix(color, vec3(luma), uBwMix * (1.0 - chromaKeep));

  // 2. High-contrast levels: crush blacks, push highlights.
  color = clamp((color - uBlackPoint) / max(uWhitePoint - uBlackPoint, 1e-4), 0.0, 1.0);

  // 3. Slight sepia lift over the mono grade.
  color = mix(color, clamp(color * uSepiaTint, 0.0, 1.0), uSepiaAmount);

  // 4. Animated film grain — re-seeded every frame, gentler in highlights.
  vec2 grainCell = vUv * uResolution / max(uGrainSizePx, 0.5);
  float seed = fract(uTime * GRAIN_TIME_DRIFT) * GRAIN_SEED_SPREAD;
  float noise = hash12(grainCell + vec2(seed, -seed)) - 0.5;
  float gradedLuma = dot(color, LUMA_WEIGHTS);
  float response = 1.0 - gradedLuma * GRAIN_HIGHLIGHT_SUPPRESSION;
  color += noise * uGrainIntensity * response;

  // 5. Vignette.
  float radial = length(vUv - 0.5) * 2.0;
  float vignette = smoothstep(VIGNETTE_INNER, VIGNETTE_OUTER, radial);
  color *= 1.0 - vignette * uVignetteStrength;

  gl_FragColor = vec4(clamp(color, 0.0, 1.0), texel.a);
}
`
